'use client';

import { cn } from '@util/index';
import { CheckIcon, ChevronRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { CategoryDocument } from 'models/category';
import { isVehicleCategory } from 'models/product';
import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useCreateListing } from '../_context';
import { canListInCategory, useCategoryTree } from '../_hooks/useCategoryTree';

const sectionTitle = 'text-[1.75rem] font-semibold text-black';

type CreateListingForm = ReturnType<typeof useCreateListing>['form'];

/** Writes the chosen category's product fields into the listing form. */
export function applyCategoryToForm(
  form: CreateListingForm,
  lastCat: CategoryDocument
) {
  form.setValue('ride_types', lastCat.ride_types!);
  form.setValue('category', lastCat.product_cats.category!);
  if (!isVehicleCategory(lastCat.product_cats.category)) {
    form.setValue('category1', lastCat.product_cats.category1!);
    form.setValue('category2', lastCat.product_cats.category2!);
    form.setValue('category3', lastCat.product_cats.category3!);
  }
  form.setValue(
    'categories',
    [
      lastCat.product_cats.category!,
      lastCat.product_cats.category1!,
      lastCat.product_cats.category2!,
      lastCat.product_cats.category3!,
    ].filter(Boolean)
  );
}

/**
 * The last category of `categoryInfo` when the path is complete enough to
 * list under, otherwise null.
 */
export function useSelectedCategory() {
  const { categoryInfo } = useCreateListing();
  const lastCat = categoryInfo[categoryInfo.length - 1];
  return lastCat && canListInCategory(categoryInfo) ? lastCat : null;
}

export default function CategoryPicker({
  panelClassName,
  autoFocus = false,
}: {
  panelClassName?: string;
  autoFocus?: boolean;
}) {
  const { form, categoryInfo, setCategoryInfo } = useCreateListing();
  const sport = categoryInfo[0];
  const tree = useCategoryTree(sport?.id);
  const selected = useSelectedCategory();
  const idPrefix = useId();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [browseId, setBrowseId] = useState(sport?.id ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedQuery = query.trim();
  const results = tree.search(trimmedQuery);
  const showResults = !!trimmedQuery;
  const showBrowse = !trimmedQuery && !selected;
  const showSelected = !trimmedQuery && !!selected;

  const requestedChain = tree.getChain(browseId);
  const browseChain = requestedChain.length
    ? requestedChain
    : tree.getChain(sport?.id ?? '');
  const browseNode = browseChain[browseChain.length - 1] ?? sport;
  const browseChildren = browseNode ? tree.getChildren(browseNode.id) : [];
  const atRoot = browseChain.length <= 1;
  const canUseBrowseNode =
    !!browseNode && browseChain.length > 1 && tree.isSelectable(browseNode);

  const selectedParentPath = tree.pathLabel(categoryInfo.slice(1, -1));
  const resultsId = `${idPrefix}-results`;
  const optionId = (i: number) => `${idPrefix}-option-${i}`;
  const activeOptionId = results.length ? optionId(activeIndex) : undefined;
  const loadingRows = (
    <div className="flex flex-col gap-[1rem] p-[1rem]">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-[5rem] animate-pulse rounded-[1rem] bg-[#F5F5F5]"
        />
      ))}
    </div>
  );
  const resultsLabel = `${results.length} ${
    results.length === 1 ? 'match' : 'matches'
  }`;

  useEffect(() => {
    if (autoFocus && window.matchMedia('(min-width: 640px)').matches) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    document
      .getElementById(optionId(activeIndex))
      ?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Re-anchors the browser when the sport or the chosen category changes, so
  // "Change" lands next to the previous pick
  useEffect(() => {
    if (selected && categoryInfo.length > 1) {
      setBrowseId(categoryInfo[categoryInfo.length - 2].id);
    } else {
      setBrowseId(sport?.id ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport?.id, selected?.id]);

  const selectCategory = (category: CategoryDocument) => {
    const chain = tree.getChain(category.id);
    if (!chain.length) return;
    setCategoryInfo(chain);
    setQuery('');
    setActiveIndex(0);
    form.clearErrors('category');
  };

  const clearSelection = () => {
    if (sport) setCategoryInfo([sport]);
    inputRef.current?.focus();
  };

  const openNode = (category: CategoryDocument) => {
    if (tree.getChildren(category.id).length) setBrowseId(category.id);
    else selectCategory(category);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && query) {
      e.stopPropagation();
      setQuery('');
      setActiveIndex(0);
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectCategory(results[activeIndex]?.category ?? results[0].category);
    }
  };

  return (
    <div className="flex flex-col gap-[2rem]">
      <div className="relative flex w-full items-center overflow-hidden rounded-[1.2rem] border border-gray-600 bg-white">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Search categories"
          aria-expanded={showResults}
          aria-controls={resultsId}
          aria-activedescendant={showResults ? activeOptionId : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search categories"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleSearchKeyDown}
          className="h-[5.5rem] min-w-0 grow border-none bg-white px-[2rem] text-[2rem] outline-none focus:ring-0"
        />
        {query ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            className="mr-[1rem] flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-full hover:bg-[#F5F5F5]"
          >
            <XIcon aria-hidden="true" className="h-[2.5rem] w-[2.5rem]" />
          </button>
        ) : (
          <SearchIcon
            aria-hidden="true"
            className="mr-[2rem] h-[2.5rem] w-[2.5rem] shrink-0"
          />
        )}
      </div>

      <div
        className={cn(
          'h-[50rem] overflow-y-auto rounded-[1.5rem] border border-[#ededed] bg-white',
          panelClassName
        )}
      >
        {showResults && (
          <div className="flex flex-col p-[1rem]">
            {!!results.length && (
              <p className="px-[1rem] pb-[0.5rem] pt-[1rem] text-[1.5rem] font-medium text-[#7F7F7F]">
                {resultsLabel}
              </p>
            )}
            {tree.isLoading && loadingRows}
            <ul id={resultsId} role="listbox" aria-label="Matching categories">
              {results.map((r, i) => (
                <li
                  key={r.category.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === activeIndex}
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => selectCategory(r.category)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex min-h-[6rem] w-full items-center gap-[1.5rem] rounded-[1rem] px-[1rem] py-[1rem] text-left ${
                      i === activeIndex ? 'bg-[#F5F5F5]' : 'bg-transparent'
                    }`}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-[1.75rem] font-semibold leading-[1.5] text-black">
                        {r.highlight.before}
                        {r.highlight.match && (
                          <mark className="bg-transparent text-brand-secondary">
                            {r.highlight.match}
                          </mark>
                        )}
                        {r.highlight.after}
                      </span>
                      {!!r.parentPath && (
                        <span className="truncate text-[1.5rem] font-medium leading-[1.4] text-[#7F7F7F]">
                          {r.parentPath}
                        </span>
                      )}
                    </span>
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="h-[2rem] w-[2rem] shrink-0 text-[#7F7F7F]"
                    />
                  </button>
                </li>
              ))}
            </ul>
            {!results.length && !tree.isLoading && (
              <div className="flex flex-col items-start gap-[1rem] p-[1rem]">
                <p className="text-[1.75rem] font-semibold text-black">
                  No category matches &ldquo;{trimmedQuery}&rdquo;
                </p>
                <p className="text-[1.5rem] font-medium text-[#7F7F7F]">
                  Try a shorter word, or browse all {sport?.name} categories.
                </p>
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="mt-[0.5rem] text-[1.75rem] font-bold text-brand-secondary"
                >
                  Browse categories
                </button>
              </div>
            )}
          </div>
        )}

        {showBrowse && (
          <div className="flex flex-col p-[1rem]">
            {atRoot && !!tree.popular.length && (
              <div className="flex flex-col gap-[1rem] border-b border-[#ededed] px-[1rem] pb-[2rem] pt-[1rem]">
                <p className={sectionTitle}>Popular in {sport?.name}</p>
                <div className="flex flex-wrap gap-[1rem]">
                  {tree.popular.map((p) => (
                    <button
                      key={p.category.id}
                      type="button"
                      onClick={() => selectCategory(p.category)}
                      className="rounded-[1rem] border border-zinc-200 px-[1.5rem] py-[0.75rem] text-[1.5rem] font-medium text-black transition-colors hover:border-brand-secondary hover:text-brand-secondary"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <nav
              aria-label="Category path"
              className="flex flex-wrap items-center gap-[0.5rem] px-[1rem] pb-[0.5rem] pt-[1.5rem] text-[1.75rem]"
            >
              {browseChain.map((c, i) => {
                const isLast = i === browseChain.length - 1;
                return (
                  <span key={c.id} className="flex items-center gap-[0.5rem]">
                    {i > 0 && (
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="h-[1.75rem] w-[1.75rem] text-[#7F7F7F]"
                      />
                    )}
                    {isLast ? (
                      <span
                        aria-current="location"
                        className="font-semibold text-black"
                      >
                        {c.name}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setBrowseId(c.id)}
                        className="font-bold text-brand-secondary"
                      >
                        {c.name}
                      </button>
                    )}
                  </span>
                );
              })}
            </nav>

            {canUseBrowseNode && browseNode && (
              <button
                type="button"
                onClick={() => selectCategory(browseNode)}
                className="my-[0.5rem] flex min-h-[5.5rem] w-full items-center gap-[1rem] rounded-[1rem] border border-dashed border-zinc-300 px-[1rem] py-[1rem] text-left transition-colors hover:border-brand-secondary"
              >
                <CheckIcon
                  aria-hidden="true"
                  className="h-[2rem] w-[2rem] shrink-0 text-brand-secondary"
                />
                <span className="text-[1.75rem] font-semibold text-black">
                  Use &ldquo;{browseNode.name}&rdquo;
                </span>
              </button>
            )}

            {tree.isLoading && loadingRows}

            {browseChildren.map((c) => {
              const kids = tree.getChildren(c.id).length;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openNode(c)}
                  className="flex min-h-[5.5rem] w-full items-center gap-[1.5rem] rounded-[1rem] px-[1rem] py-[1rem] text-left hover:bg-[#F5F5F5]"
                >
                  <span className="min-w-0 flex-1 text-[1.75rem] font-medium text-black">
                    {c.name}
                  </span>
                  {kids > 0 ? (
                    <>
                      <span className="shrink-0 text-[1.5rem] font-medium text-[#7F7F7F]">
                        {kids} {kids === 1 ? 'type' : 'types'}
                      </span>
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="h-[2rem] w-[2rem] shrink-0 text-[#7F7F7F]"
                      />
                    </>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="h-[2rem] w-[2rem] shrink-0 rounded-full border-[1px] border-zinc-400"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {showSelected && selected && (
          <div className="flex flex-col gap-[1.5rem] p-[2rem]">
            <p className={sectionTitle}>Your listing goes in</p>
            <div className="flex items-center gap-[1.5rem] rounded-[1.5rem] border border-brand-secondary p-[2rem]">
              <span className="flex h-[3rem] w-[3rem] shrink-0 items-center justify-center rounded-full bg-brand-secondary">
                <CheckIcon
                  aria-hidden="true"
                  strokeWidth={3}
                  className="h-[1.75rem] w-[1.75rem] text-white"
                />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-[2rem] font-semibold leading-[1.5] text-black">
                  {selected.name}
                </span>
                {!!selectedParentPath && (
                  <span className="text-[1.5rem] font-medium leading-[1.4] text-[#444]">
                    {selectedParentPath}
                  </span>
                )}
              </span>
              {categoryInfo.length > 1 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  aria-label="Change category"
                  className="shrink-0 text-[1.75rem] font-bold text-brand-secondary"
                >
                  Change
                </button>
              )}
            </div>
            <p className="text-[1.5rem] font-medium text-[#7F7F7F]">
              Buyers browsing {selected.name} will see your listing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
